/**
 * @file data/countryGuides.js
 * @description Comprehensive country travel guides with detailed information
 * for each destination including attractions, food, transport, and tips.
 */

const countries = [
  {
    code: "TH",
    name: "Thailand",
    continent: "Asia",
    featured: true
  },
  {
    code: "SG",
    name: "Singapore",
    continent: "Asia",
    featured: true
  },
  {
    code: "AE",
    name: "United Arab Emirates",
    continent: "Asia",
    featured: true
  },
  {
    code: "MY",
    name: "Malaysia",
    continent: "Asia"
  },
  {
    code: "ID",
    name: "Indonesia",
    continent: "Asia",
    featured: true
  },
  {
    code: "US",
    name: "United States",
    continent: "North America",
    featured: true
  },
  {
    code: "CA",
    name: "Canada",
    continent: "North America"
  },
  {
    code: "GB",
    name: "United Kingdom",
    continent: "Europe",
    featured: true
  },
  {
    code: "AU",
    name: "Australia",
    continent: "Oceania",
    featured: true
  },
  {
    code: "FR",
    name: "France",
    continent: "Europe",
    featured: true
  },
  {
    code: "CH",
    name: "Switzerland",
    continent: "Europe",
    featured: true
  },
  {
    code: "MV",
    name: "Maldives",
    continent: "Asia",
    featured: true
  },
  {
    code: "LK",
    name: "Sri Lanka",
    continent: "Asia"
  },
  {
    code: "NP",
    name: "Nepal",
    continent: "Asia"
  },
  {
    code: "BT",
    name: "Bhutan",
    continent: "Asia"
  },
  {
    code: "DE",
    name: "Germany",
    continent: "Europe"
  },
  {
    code: "NZ",
    name: "New Zealand",
    continent: "Oceania"
  },
  {
    code: "IT",
    name: "Italy",
    continent: "Europe",
    featured: true
  },
  {
    code: "ES",
    name: "Spain",
    continent: "Europe"
  },
  {
    code: "GR",
    name: "Greece",
    continent: "Europe",
    featured: true
  },
  {
    code: "TR",
    name: "Turkey",
    continent: "Europe"
  },
  {
    code: "VN",
    name: "Vietnam",
    continent: "Asia"
  },
  {
    code: "JP",
    name: "Japan",
    continent: "Asia",
    featured: true
  },
  {
    code: "KR",
    name: "South Korea",
    continent: "Asia"
  },
  {
    code: "EG",
    name: "Egypt",
    continent: "Africa",
    featured: true
  },
  {
    code: "RU",
    name: "Russia",
    continent: "Europe"
  },
  {
    code: "NL",
    name: "Netherlands",
    continent: "Europe"
  },
  {
    code: "BE",
    name: "Belgium",
    continent: "Europe"
  },
  {
    code: "AT",
    name: "Austria",
    continent: "Europe"
  },
  {
    code: "CZ",
    name: "Czech Republic",
    continent: "Europe"
  },
  {
    code: "PT",
    name: "Portugal",
    continent: "Europe"
  },
  {
    code: "IE",
    name: "Ireland",
    continent: "Europe"
  },
  {
    code: "FI",
    name: "Finland",
    continent: "Europe"
  },
  {
    code: "SE",
    name: "Sweden",
    continent: "Europe"
  },
  {
    code: "NO",
    name: "Norway",
    continent: "Europe"
  },
  {
    code: "DK",
    name: "Denmark",
    continent: "Europe"
  },
  {
    code: "KH",
    name: "Cambodia",
    continent: "Asia"
  },
  {
    code: "LA",
    name: "Laos",
    continent: "Asia"
  },
  {
    code: "PH",
    name: "Philippines",
    continent: "Asia"
  },
  {
    code: "MU",
    name: "Mauritius",
    continent: "Africa"
  },
  {
    code: "SC",
    name: "Seychelles",
    continent: "Africa"
  },
  {
    code: "FJ",
    name: "Fiji",
    continent: "Oceania"
  },
  {
    code: "ZA",
    name: "South Africa",
    continent: "Africa"
  },
  {
    code: "KE",
    name: "Kenya",
    continent: "Africa"
  },
  {
    code: "TZ",
    name: "Tanzania",
    continent: "Africa"
  },
  {
    code: "MA",
    name: "Morocco",
    continent: "Africa"
  },
  {
    code: "IS",
    name: "Iceland",
    continent: "Europe"
  },
  {
    code: "HR",
    name: "Croatia",
    continent: "Europe"
  },
  {
    code: "HU",
    name: "Hungary",
    continent: "Europe"
  },
  {
    code: "PL",
    name: "Poland",
    continent: "Europe"
  }
];

const createCountryGuideTemplate = (code, name, continent) => ({
  name,
  continent,
  overview: `${name} offers visitors a unique blend of cultural experiences, natural beauty, and modern attractions. This comprehensive guide will help you plan your perfect trip to this fascinating destination.`,
  images: {
    banner: `/images/countries/${code}/banner.jpg`,
    gallery: [
      {
        url: `/images/countries/${code}/gallery1.jpg`,
        caption: `Exploring ${name}`
      },
      {
        url: `/images/countries/${code}/gallery2.jpg`,
        caption: "Local Culture"
      },
      {
        url: `/images/countries/${code}/gallery3.jpg`,
        caption: "Natural Beauty"
      }
    ]
  },
  bestTimeToVisit: {
    peak: "June to August",
    shoulder: "April-May, September-October",
    lowSeason: "November to March",
    notes: "Weather and tourist seasons vary by region. Check specific destinations for detailed information."
  },
  visaRequirements: {
    general: `Entry requirements for ${name} vary depending on your nationality and purpose of visit.`,
    requirements: [
      "Valid passport with 6 months validity",
      "Completed visa application form",
      "Proof of accommodation",
      "Return ticket",
      "Sufficient funds"
    ],
    fees: "Varies by nationality and visa type",
    processingTime: "5-15 business days"
  },
  popularDestinations: [
    {
      name: "Popular Destination 1",
      description: "Description of the first popular destination",
      highlights: [
        "Highlight 1",
        "Highlight 2",
        "Highlight 3",
        "Highlight 4",
        "Highlight 5"
      ],
      bestTimeToVisit: "Year-round",
      images: [
        `/images/countries/${code}/dest1.jpg`,
        `/images/countries/${code}/dest2.jpg`
      ]
    },
    {
      name: "Popular Destination 2",
      description: "Description of the second popular destination",
      highlights: [
        "Highlight 1",
        "Highlight 2",
        "Highlight 3",
        "Highlight 4",
        "Highlight 5"
      ],
      bestTimeToVisit: "Seasonal",
      images: [
        `/images/countries/${code}/dest3.jpg`,
        `/images/countries/${code}/dest4.jpg`
      ]
    },
    {
      name: "Popular Destination 3",
      description: "Description of the third popular destination",
      highlights: [
        "Highlight 1",
        "Highlight 2",
        "Highlight 3",
        "Highlight 4",
        "Highlight 5"
      ],
      bestTimeToVisit: "Year-round",
      images: [
        `/images/countries/${code}/dest5.jpg`,
        `/images/countries/${code}/dest6.jpg`
      ]
    }
  ],
  culture: {
    traditions: [
      "Traditional custom 1",
      "Traditional custom 2",
      "Traditional custom 3",
      "Traditional custom 4",
      "Traditional custom 5"
    ],
    etiquette: [
      "Etiquette tip 1",
      "Etiquette tip 2",
      "Etiquette tip 3",
      "Etiquette tip 4",
      "Etiquette tip 5"
    ],
    festivals: [
      {
        name: "Major Festival 1",
        description: "Description of the first major festival",
        date: "Annual date or season"
      },
      {
        name: "Major Festival 2",
        description: "Description of the second major festival",
        date: "Annual date or season"
      },
      {
        name: "Major Festival 3",
        description: "Description of the third major festival",
        date: "Annual date or season"
      }
    ]
  },
  cuisine: {
    mustTry: [
      {
        name: "Local Dish 1",
        description: "Description of the first must-try dish"
      },
      {
        name: "Local Dish 2",
        description: "Description of the second must-try dish"
      },
      {
        name: "Local Dish 3",
        description: "Description of the third must-try dish"
      }
    ],
    diningTips: [
      "Try local specialties",
      "Look for busy restaurants",
      "Check reviews online",
      "Be adventurous with street food"
    ],
    regions: [
      {
        name: "Culinary Region 1",
        specialties: ["Specialty 1", "Specialty 2", "Specialty 3"]
      },
      {
        name: "Culinary Region 2",
        specialties: ["Specialty 1", "Specialty 2", "Specialty 3"]
      }
    ]
  },
  transportation: {
    public: {
      types: [
        {
          name: "Public Transit",
          description: "Local transportation network",
          cost: "Varies by distance"
        },
        {
          name: "Taxi",
          description: "Available in major cities",
          cost: "Metered or fixed rates"
        }
      ],
      tips: [
        "Use public transport for cost savings",
        "Book tickets in advance when possible",
        "Consider travel passes for longer stays",
        "Download local transport apps"
      ]
    },
    intercity: {
      types: ["Bus", "Train", "Domestic flights", "Car rental"],
      recommended: "Depends on distance and destination"
    }
  },
  accommodation: {
    types: [
      {
        category: "Luxury",
        priceRange: "$$$$",
        examples: ["5-star hotels", "Luxury resorts", "Boutique hotels"]
      },
      {
        category: "Mid-range",
        priceRange: "$$$",
        examples: ["3-4 star hotels", "Guesthouses", "Serviced apartments"]
      },
      {
        category: "Budget",
        priceRange: "$$",
        examples: ["Hostels", "Budget hotels", "Homestays"]
      }
    ],
    bookingTips: [
      "Book in advance for peak season",
      "Check reviews carefully",
      "Consider location and transport",
      "Look for package deals"
    ]
  },
  budgetInfo: {
    daily: {
      budget: "$50-100",
      midRange: "$100-300",
      luxury: "$300+"
    },
    expenses: {
      meals: {
        budget: "$10-20",
        restaurant: "$20-50",
        upscale: "$50+"
      },
      accommodation: {
        budget: "$30-50",
        midRange: "$50-150",
        luxury: "$150+"
      },
      transportation: {
        public: "$5-10 per day",
        taxi: "Starting from $10",
        tourBus: "$50-100"
      }
    },
    moneyTips: [
      "Exchange money at official places",
      "Carry some cash for small purchases",
      "Check card acceptance beforehand",
      "Keep emergency funds separate"
    ]
  },
  safetyTips: {
    general: [
      "Stay aware of surroundings",
      "Keep valuables secure",
      "Follow local guidelines"
    ],
    healthPrecautions: [
      "Check required vaccinations",
      "Carry travel insurance",
      "Know emergency numbers"
    ],
    emergencyContacts: {
      police: "Check local emergency numbers",
      ambulance: "Check local emergency numbers",
      tourist: "Check tourist police numbers"
    }
  },
  highlights: [
    {
      name: "Cultural Attractions",
      description: "Explore local heritage and traditions",
      bestTime: "Year-round",
      image: `/images/countries/${code}/highlight1.jpg`
    },
    {
      name: "Natural Wonders",
      description: "Discover beautiful landscapes",
      bestTime: "Seasonal",
      image: `/images/countries/${code}/highlight2.jpg`
    },
    {
      name: "Local Experiences",
      description: "Immerse in local life and customs",
      bestTime: "Year-round",
      image: `/images/countries/${code}/highlight3.jpg`
    }
  ],
  theme: {
    primary: "#5365dc",
    secondary: "#243185",
    accent: "#ffd700"
  }
});

// Initialize guides for all countries
const guides = {};
countries.forEach(country => {
  const countryCode = country.code.toLowerCase();
  guides[countryCode] = createCountryGuideTemplate(
    countryCode,
    country.name,
    country.continent
  );
});

export const countryGuides = guides;

export const getCountryTheme = (countryCode) => {
  const websiteTheme = {
    primary: "#5365dc",
    secondary: "#243185",
    accent: "#ffd700"
  };
  return websiteTheme;
};

export const getPopularCountries = () => {
  return Object.keys(countryGuides).filter(code => 
    ["th", "sg", "ae", "jp", "fr", "us", "gb", "au"].includes(code)
  );
};

export const getCountryByContinent = (continent) => {
  return Object.entries(countryGuides)
    .filter(([_, guide]) => guide.continent === continent)
    .map(([code]) => code);
};

export const updateCountryGuide = (countryCode, data) => {
  if (countryGuides[countryCode]) {
    countryGuides[countryCode] = {
      ...countryGuides[countryCode],
      ...data
    };
    return true;
  }
  return false;
};

export const addNewCountry = (countryCode, data) => {
  if (!countryGuides[countryCode]) {
    const countryData = countries.find(c => c.code.toLowerCase() === countryCode);
    if (countryData) {
      countryGuides[countryCode] = createCountryGuideTemplate(
        countryCode,
        countryData.name,
        countryData.continent
      );
      return true;
    }
  }
  return false;
};

export const deleteCountry = (countryCode) => {
  if (countryGuides[countryCode]) {
    delete countryGuides[countryCode];
    return true;
  }
  return false;
};

export const getAllCountries = () => {
  return Object.entries(countryGuides).map(([code, data]) => ({
    code,
    name: data.name
  }));
}; 